import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import BotanicalArt from "@/components/BotanicalArt";

const INCLUDED = [
  "A qualified, verified psychologist",
  "Secure video or audio session — 30 to 60 minutes",
  "Complete confidentiality — no chat records kept",
  "Free rescheduling over WhatsApp",
  "The same price for every therapist",
];

const NEVER = [
  "Fake \"first session\" discounts that balloon later",
  "Countdown timers or manufactured urgency",
  "Surprise platform or convenience fees",
  "Lock-in packages you didn't ask for",
];

export default function Pricing() {
  return (
    <div data-testid="pricing-page">
      <section className="mx-auto max-w-screen-2xl px-6 py-16 md:px-12 md:py-24 lg:px-24">
        <Reveal>
          <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-accent">
            <span className="h-px w-10 bg-accent" /> Pricing
          </p>
          <h1 className="mt-6 max-w-3xl font-serif text-4xl font-bold leading-tight tracking-tight md:text-6xl">
            Professional care shouldn't feel out of reach.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
            Honest prices. Every therapist, every day of the week.
            Simple enough that money is never the reason you don't talk to someone.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-screen-2xl px-6 pb-24 md:px-12 md:pb-32 lg:px-24">
        <div className="grid gap-8 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="h-full rounded-3xl border border-primary/10 bg-primary p-10 text-primary-foreground md:p-14" data-testid="pricing-main-card">
              <div className="flex items-center justify-between gap-4">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary-foreground/60">Start your therapy at</p>
                <span className="rounded-full bg-primary-foreground/15 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-primary-foreground" data-testid="pricing-start-here">Start here</span>
              </div>
              <p className="mt-6 font-serif text-8xl font-bold tracking-tight">₹99</p>
              <p className="mt-3 font-serif text-xl italic text-primary-foreground/70">Your first session</p>
              <p className="mt-8 text-sm leading-relaxed text-primary-foreground/80">
                Same ₹99 price for every therapist — no hidden tiers, no surprises.
              </p>
              <Link
                to="/find"
                data-testid="pricing-page-cta"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-primary-foreground px-8 py-4 text-sm font-bold text-primary transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                Find a therapist
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>

          <div className="grid gap-8 lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-primary/10 bg-card p-10" data-testid="pricing-included">
                <h2 className="font-serif text-2xl font-bold tracking-tight">What every session includes</h2>
                <ul className="mt-6 space-y-4">
                  {INCLUDED.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm md:text-base">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="rounded-3xl border border-primary/10 bg-card p-10" data-testid="pricing-never">
                <h2 className="font-serif text-2xl font-bold tracking-tight">What you'll never see here</h2>
                <ul className="mt-6 space-y-4">
                  {NEVER.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm md:text-base text-muted-foreground">
                      <X className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-8 grid gap-8 md:grid-cols-3">
            <div className="rounded-2xl border border-primary/10 bg-background p-8" data-testid="pricing-how-pay">
              <h3 className="font-serif text-xl font-bold tracking-tight">How payment works</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Book your slot first. A secure payment link arrives by email or WhatsApp —
                pay only when you're ready, before your session begins.
              </p>
            </div>
            <div className="rounded-2xl border border-primary/10 bg-background p-8" data-testid="pricing-reschedule">
              <h3 className="font-serif text-xl font-bold tracking-tight">Rescheduling</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                Life happens. Message us on WhatsApp and we'll move your session to a
                better time — free, no explanations needed.
              </p>
            </div>
            <div className="rounded-2xl border border-primary/10 bg-background p-8" data-testid="pricing-not-right">
              <h3 className="font-serif text-xl font-bold tracking-tight">If it doesn't feel right</h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                You can switch therapists anytime at no extra cost. The relationship matters
                more than the booking.
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-8 grid items-center gap-8 rounded-3xl border border-primary/10 bg-secondary p-8 md:grid-cols-12 md:p-12" data-testid="pricing-art-band">
            <div className="md:col-span-7">
              <h2 className="font-serif text-3xl font-bold tracking-tight">Start small. One conversation is enough.</h2>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-muted-foreground md:text-base">
                No packages, no pressure. Book a single session at ₹99 and see how
                it feels — growth from there happens at your pace.
              </p>
            </div>
            <BotanicalArt variant="sprout" className="md:col-span-5" />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
